import { DOCUMENT_KINDS, HACIENDA_STATUSES, labelOf } from '@facture/contracts'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { DownloadIcon, FileCodeIcon, SearchXIcon, ShieldCheckIcon } from 'lucide-react'
import { AuthShell } from '@/components/app/auth-shell'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { api } from '@/lib/api'
import { formatDate, formatMoney } from '@/lib/format'

interface PublicDocument {
  company: { name: string; identification: string }
  kind: string
  consecutive: string | null
  key: string | null
  issuedAt: string | null
  currency: string
  total: number
  taxTotal: number
  haciendaStatus: string
  receiverName: string | null
  hasXml: boolean
  lines: { description: string; quantity: number; unitPrice: number; total: number }[]
}

export const Route = createFileRoute('/p/$token')({ component: PublicDocumentPage })

function PublicDocumentPage() {
  const { token } = Route.useParams()
  const { data, isLoading, isError } = useQuery({
    queryKey: ['public-document', token],
    queryFn: () => api.get<PublicDocument>(`/public/documents/${token}`, { global: true }),
    retry: false,
  })

  if (isLoading) {
    return (
      <AuthShell card={false}>
        <Skeleton className="h-72 rounded-2xl" />
      </AuthShell>
    )
  }
  if (isError || !data) {
    return (
      <AuthShell card={false}>
        <div className="text-center">
          <div className="mx-auto flex size-12 items-center justify-center rounded-2xl bg-muted text-muted-foreground">
            <SearchXIcon className="size-6" />
          </div>
          <h1 className="mt-5 font-semibold text-2xl tracking-tight">Comprobante no disponible</h1>
          <p className="mt-2 text-muted-foreground text-sm">
            El enlace no es válido o el comprobante ya no existe.
          </p>
        </div>
      </AuthShell>
    )
  }

  const accepted = data.haciendaStatus === 'accepted'
  const money = (n: number) => formatMoney(n, data.currency as 'CRC' | 'USD' | 'EUR')
  const base = `/api/public/documents/${token}`

  return (
    <AuthShell card={false}>
      <div className="-mx-2 rounded-2xl border border-border bg-card p-6 shadow-elevation-2">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-muted-foreground text-xs">{data.company.name}</div>
            <h1 className="mt-0.5 font-semibold text-xl tracking-tight">
              {labelOf(DOCUMENT_KINDS, data.kind)}
            </h1>
            <div className="mt-1 font-mono text-muted-foreground text-xs">{data.consecutive}</div>
          </div>
          <Badge variant={accepted ? 'success' : 'neutral'}>
            {accepted ? <ShieldCheckIcon className="size-3.5" /> : null}
            {labelOf(HACIENDA_STATUSES, data.haciendaStatus)}
          </Badge>
        </div>

        <dl className="mt-5 grid grid-cols-2 gap-3 text-sm">
          <div>
            <dt className="text-muted-foreground text-xs">Fecha</dt>
            <dd>{data.issuedAt ? formatDate(data.issuedAt) : '—'}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground text-xs">Cliente</dt>
            <dd className="truncate">{data.receiverName ?? 'Consumidor final'}</dd>
          </div>
        </dl>

        <div className="mt-5 divide-y divide-border rounded-xl border border-border text-sm">
          {data.lines.map((l, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: líneas de solo lectura
            <div key={i} className="flex items-start justify-between gap-3 px-3 py-2.5">
              <div className="min-w-0">
                <div className="truncate font-medium">{l.description}</div>
                <div className="text-muted-foreground text-xs">
                  {l.quantity} × {money(l.unitPrice)}
                </div>
              </div>
              <div className="shrink-0 tabular">{money(l.total)}</div>
            </div>
          ))}
        </div>

        <div className="mt-4 flex items-end justify-between border-border border-t pt-4">
          <div className="text-muted-foreground text-xs">
            IVA {money(data.taxTotal)}
            <br />
            Total
          </div>
          <div className="font-semibold text-2xl tabular tracking-tight">{money(data.total)}</div>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-2">
          <Button asChild>
            <a href={`${base}/pdf`} target="_blank" rel="noreferrer">
              <DownloadIcon /> PDF
            </a>
          </Button>
          <Button asChild variant="outline" disabled={!data.hasXml}>
            <a href={`${base}/xml`}>
              <FileCodeIcon /> XML
            </a>
          </Button>
        </div>
        {data.key && (
          <p className="mt-4 break-all text-center font-mono text-[10px] text-muted-foreground">{data.key}</p>
        )}
      </div>
    </AuthShell>
  )
}
